import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import api, { setAuthRefresher } from '../lib/api';

const AuthContext = createContext(null);

// Refresh this many seconds before the access token's `exp` claim.
const REFRESH_LEEWAY_SECONDS = 60;

const decodeJwtExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
};

const isTokenExpired = (token, leewaySeconds = 0) => {
  const exp = decodeJwtExp(token);
  if (!exp) return true;
  return exp * 1000 <= Date.now() + leewaySeconds * 1000;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const refreshInFlightRef = useRef(null);

  const clearTokens = () => {
    localStorage.removeItem('kt_token');
    localStorage.removeItem('kt_refresh');
  };

  const persistTokens = (data) => {
    if (data.access_token) localStorage.setItem('kt_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('kt_refresh', data.refresh_token);
  };

  const fetchMe = async () => {
    const { data } = await api.get('/auth/me');
    setUser(data);
    return data;
  };

  const refreshTokens = useCallback(async () => {
    // De-dupe concurrent refreshes — multiple tabs/requests race-safe within this tab.
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const refreshToken = localStorage.getItem('kt_refresh');
    if (!refreshToken) throw new Error('No refresh token');

    const body = new URLSearchParams();
    body.set('refresh_token', refreshToken);
    const promise = api
      .post('/auth/refresh', body, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        // Prevent the interceptor from looping if /auth/refresh itself returns 401.
        _skipAuthRefresh: true,
      })
      .then(({ data }) => {
        persistTokens(data);
        scheduleRefresh(data.access_token);
        return data.access_token;
      })
      .catch((err) => {
        clearTokens();
        setUser(null);
        throw err;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });

    refreshInFlightRef.current = promise;
    return promise;
    // ``scheduleRefresh`` is declared below and only called asynchronously, so
    // it cannot go in the dep array — naming it here would read the const
    // before initialisation on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleRefresh = useCallback((accessToken) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const exp = decodeJwtExp(accessToken);
    if (!exp) return;
    const msUntilRefresh = exp * 1000 - Date.now() - REFRESH_LEEWAY_SECONDS * 1000;
    if (msUntilRefresh <= 0) {
      refreshTokens().catch(() => {});
      return;
    }
    refreshTimerRef.current = setTimeout(() => {
      refreshTokens().catch(() => {});
    }, msUntilRefresh);
  }, [refreshTokens]);

  // Expose the refresher to the axios interceptor (set once on mount).
  useEffect(() => {
    setAuthRefresher(refreshTokens);
  }, [refreshTokens]);

  // Initial load: if access token is expired but we have a refresh token, refresh first.
  useEffect(() => {
    const token = localStorage.getItem('kt_token');
    const refresh = localStorage.getItem('kt_refresh');

    const bootstrap = async () => {
      if (!token && !refresh) return;
      if (token && !isTokenExpired(token, REFRESH_LEEWAY_SECONDS)) {
        scheduleRefresh(token);
        await fetchMe();
        return;
      }
      // Access token missing or near/past expiry — try refresh.
      if (refresh) {
        await refreshTokens();
        await fetchMe();
      }
    };

    bootstrap()
      .catch(() => clearTokens())
      .finally(() => setLoading(false));

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab focus / visibility: handle laptop sleep and long-closed tabs.
  useEffect(() => {
    const onFocus = () => {
      const accessToken = localStorage.getItem('kt_token');
      if (!accessToken) return;
      if (isTokenExpired(accessToken, REFRESH_LEEWAY_SECONDS)) {
        refreshTokens().catch(() => {});
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshTokens]);

  const login = async (email, password) => {
    const body = new URLSearchParams();
    body.set('username', email);
    body.set('password', password);
    const { data } = await api.post('/auth/token', body, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    persistTokens(data);
    scheduleRefresh(data.access_token);
    await fetchMe();
  };

  const logout = async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    clearTokens();
    setUser(null);
    try { await api.post('/auth/logout'); } catch (_) {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
